import React, { useState, useEffect } from 'react'; 
import { auth, db } from "../firebase"; 
import { useNavigate } from "react-router-dom"; 
import { 
  Heart, 
  LayoutDashboard, 
  UserCog, 
  BookOpenText, 
  Cpu, 
  Salad,
  Loader2 
} from 'lucide-react'; 
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from "firebase/firestore";
import axios from "axios";
import Header from "../components/Header"; 
import Footer from "../components/Footer";


const HomePageTile = ({ icon: Icon, title, description, onClick, bgColor }) => (
  <div
    onClick={onClick}
    className={`
      group relative overflow-hidden rounded-2xl p-6
      ${bgColor} text-white
      transform transition-all duration-300
      hover:scale-105 hover:shadow-2xl
      cursor-pointer
    `}
  >
    <div className="absolute inset-0 bg-black opacity-20 group-hover:opacity-10 transition-opacity"></div>
    <div className="relative z-10 flex flex-col h-full">
      <Icon className="w-12 h-12 mb-4 opacity-80 group-hover:animate-pulse" />
      <h3 className="text-2xl font-bold mb-2">{title}</h3>
      <p className="text-sm opacity-75 flex-grow">{description}</p>
      <div
        className="
          mt-4 self-start
          px-3 py-1
          bg-white bg-opacity-20
          rounded-full
          text-xs
          group-hover:bg-opacity-40
          transition-all
        "
      >
        Explore
      </div>
    </div>
  </div>
);

const OPENAI_API_KEY = "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";

const HomePage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    onboarding: null,
    health: null,
    meals: null,
  });
  const [tips, setTips] = useState([]);
  const [generatingTips, setGeneratingTips] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bmi, setBmi] = useState(null);

  const userName = auth.currentUser ? auth.currentUser.displayName : "Guest";

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async (user) => {
      try {
        const onboardingDoc = await getDoc(doc(db, "onboardingData", user.uid));
        const healthDoc = await getDoc(doc(db, "healthData", user.uid));
        const mealLogsDoc = await getDoc(doc(db, "mealLogs", user.uid));

        setUserData({
          onboarding: onboardingDoc.exists() ? onboardingDoc.data() : null,
          health: healthDoc.exists() ? healthDoc.data() : null,
          meals: mealLogsDoc.exists() ? mealLogsDoc.data() : null,
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
      setLoading(false);
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserData(user);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Calculate BMI
  useEffect(() => {
    if (userData.onboarding) {
      const weight = userData.onboarding.weight;
      const height = userData.onboarding.height;
      const weightInKg = userData.onboarding.weightUnit === "kg" 
        ? weight 
        : weight / 2.20462;
      const heightInMeters = userData.onboarding.heightUnit === "cm" 
        ? height / 100 
        : height * 0.3048;
      
      const calculatedBmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
      setBmi(calculatedBmi);
    }
  }, [userData.onboarding]);

  // Fetch or generate tips
  useEffect(() => {
    const checkAndGenerateTips = async () => {
      if (!auth.currentUser) return;

      const userId = auth.currentUser.uid;
      const tipDoc = await getDoc(doc(db, "healthTips", userId));
      const currentDate = new Date().toLocaleDateString();

      if (!tipDoc.exists() || 
          !tipDoc.data().timestamp || 
          new Date(tipDoc.data().timestamp).toLocaleDateString() !== currentDate) {
        await generateHealthTips();
      } else {
        setTips(tipDoc.data().tips);
      }
    };

    if (!loading && userData.onboarding) {
      checkAndGenerateTips();
    }
  }, [loading, userData.onboarding]);

  // Generate health tips
  const generateHealthTips = async () => {
    setGeneratingTips(true);
    const currentTime = new Date();
    
    try {
      const messages = [
        {
          role: "system",
          content: `You are an advanced health advisor. Generate creative and varied health tips 
          that are highly specific to the user's context and current situation. 
          Each tip should be unique and actionable.`
        },
        generatePrompt(),
      ];
  
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-3.5-turbo",
          messages,
          max_tokens: 500,
          temperature: 0.9,
          frequency_penalty: 1.0,
          presence_penalty: 1.0,
          top_p: 0.95
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${OPENAI_API_KEY}`,
          },
        }
      );
  
      const generatedContent = response.data.choices[0].message.content;
      
      const generatedTips = generatedContent
        .split(/\d+\.|\n\n/)
        .map(tip => tip.trim())
        .filter(tip => tip.length > 0)
        .map(tip => tip.replace(/^[-•\s]+/, ''));
  
      setTips(generatedTips);
  
      // Save to Firebase
      const user = auth.currentUser;
      if (user) {
        await setDoc(doc(db, "healthTips", user.uid), {
          userId: user.uid,
          tips: generatedTips,
          timestamp: new Date().toISOString(),
          metadata: {
            timeOfDay: currentTime.getHours() < 12 ? 'Morning' : currentTime.getHours() < 17 ? 'Afternoon' : 'Evening',
            dayOfWeek: currentTime.toLocaleDateString('en-US', { weekday: 'long' }),
            date: currentTime.toLocaleDateString()
          }
        });
      }
    } catch (error) {
      console.error("Error generating health tips:", error);
    } finally {
      setGeneratingTips(false);
    }
  };
  
    

  // Generate prompt for health tips
  const generatePrompt = () => {
    const currentTime = new Date();
    
    const comprehensiveTipGeneration = () => {
      const tips = [];
      const onboarding = userData.onboarding;
  
      // Substance Use Recommendations
      if (onboarding?.sleep?.caffeinatedDrinks > 2) {
        tips.push(`Caffeine Intake Management: Limit caffeine to max 2-3 drinks per day. Your current intake of ${onboarding.sleep.caffeinatedDrinks} drinks may disrupt sleep patterns and increase anxiety. Consider replacing afternoon/evening caffeinated drinks with herbal teas or decaf alternatives.`);
      }
  
      if (onboarding?.substanceUse?.alcoholAmount === '10_plus') {
        tips.push(`Alcohol Consumption Risk: Your reported alcohol intake suggests potential health risks. Aim to reduce to recommended limits: max 1 drink/day for women, 2 for men. Consider professional guidance for gradual reduction strategies.`);
      }
  
      if (onboarding?.substanceUse?.recreationalDrugs === 'daily') {
        tips.push(`Substance Use Intervention: Daily recreational drug use requires immediate professional medical consultation. Seek comprehensive addiction support through specialized counseling and medical oversight.`);
      }
  
      // Mental Health Recommendations
      const mentalHealthRisks = [
        onboarding?.mentalHealth?.feelingDown === 'several_days',
        onboarding?.mentalHealth?.littleInterest === 'several_days',
        onboarding?.mentalHealth?.stressLevel === 'high'
      ].filter(Boolean);
  
      if (mentalHealthRisks.length > 1) {
        tips.push(`Mental Wellness Intervention: Multiple indicators suggest potential mental health challenges. Implement a holistic approach:
  - Practice daily mindfulness/meditation (10-15 minutes)
  - Establish consistent sleep schedule
  - Engage in regular physical activity
  - Consider professional counseling for comprehensive mental health assessment`);
      }
  
      // Sleep Optimization
      if (onboarding?.sleep?.sleepSixToEight === 'rarely') {
        tips.push(`Sleep Health Critical: Your sleep pattern indicates chronic sleep deficit. Prioritize:
  - Consistent sleep-wake cycle
  - Create dark, cool sleeping environment
  - Limit screen time 1 hour before bedtime
  - Target 7-8 hours of quality sleep nightly
  - Consider sleep hygiene consultation`);
      }
  
      // Relationship and Social Wellness
      if (onboarding?.relationships?.feelIsolated === 'often') {
        tips.push(`Social Connection Strategy: Addressing isolation is crucial for mental and physical health.
  - Join community groups aligned with interests
  - Volunteer in local organizations
  - Develop structured social interaction plan
  - Consider support groups for shared experiences
  - Practice active communication skills`);
      }
  
      // Nutritional and Dietary Optimization
      const nutritionalInterventions = [];
      if (!onboarding?.diet?.fruitServings) {
        nutritionalInterventions.push('Increase daily fruit intake to 2-3 servings');
      }
      if (!onboarding?.diet?.vegetableServings) {
        nutritionalInterventions.push('Aim for 4-5 vegetable servings daily');
      }
      if (onboarding?.diet?.ultraProcessedFood) {
        nutritionalInterventions.push('Reduce ultra-processed food consumption by 50%');
      }
  
      if (nutritionalInterventions.length) {
        tips.push(`Nutrition Optimization Targets:
  ${nutritionalInterventions.map(tip => `- ${tip}`).join('\n')}`);
      }
  
      // Physical Activity Recommendations
      if (onboarding?.physicalActivity?.walking === 'none') {
        tips.push(`Sedentary Lifestyle Intervention: Gradually increase physical activity.
  - Start with 15-minute daily walks
  - Progress to 30 minutes moderate activity 5 days/week
  - Incorporate strength training 2-3 times weekly
  - Use step tracking for motivation`);
      }
  
      // Symptoms and Health Conditions Management
      if (onboarding?.symptoms?.includes('Abdominal Pain')) {
        tips.push(`Symptom Management - Abdominal Pain:
  - Maintain food diary to identify potential triggers
  - Consult healthcare provider for comprehensive evaluation
  - Consider anti-inflammatory diet
  - Practice stress reduction techniques`);
      }
  
      // Digital Wellness
      if (onboarding?.socialMediaAddiction?.neglectActivities) {
        tips.push(`Digital Wellness Reset:
  - Implement strict daily screen time limits
  - Use app blockers during work/personal time
  - Replace digital time with meaningful activities
  - Practice periodic digital detox
  - Develop offline hobby or skill`);
      }
  
      return tips;
    };
  
    return {
      role: "user",
      content: `Advanced Personalized Health Optimization Protocol
  
  COMPREHENSIVE HEALTH RECOMMENDATIONS
  
  Generate up to 5 highly personalized, evidence-based health optimization strategies that:
  1. Address specific individual health risks
  2. Provide actionable, measurable interventions
  3. Integrate multisystem health approach
  4. Leverage comprehensive individual health data
  
  GENERATED RECOMMENDATIONS:
  ${comprehensiveTipGeneration().map((tip, index) => `${index + 1}. ${tip}`).join('\n\n')}
  
  Recommendation Requirements:
  - Clinical precision
  - Specific numerical targets
  - Clear rationale
  - Preventative and rehabilitative strategies`
    };
  };

  const navigateTo = (path) => {
    navigate(path);
  };
  

  const tiles = [
    {
      icon: BookOpenText,
      title: "Health Journal",
      description: "Document your wellness journey, track progress, and reflect.",
      onClick: () => navigateTo('/dashboard'),
      bgColor: 'bg-gradient-to-br from-orange-600 to-orange-400'
    },
    {
      icon: Salad,
      title: "Meal Tracker",
      description: "Log and analyze your nutrition with detailed meal tracking.",
      onClick: () => navigateTo('/food'),
      bgColor: 'bg-gradient-to-br from-green-600 to-green-400'
    },

    {
      icon: Heart,
      title: "Health Profile",
      description: "Customize and manage your personal health goals and settings.",
      onClick: () => navigateTo('/profile-setup'),
      bgColor: 'bg-gradient-to-br from-pink-600 to-pink-400'
    },
    {
      icon: UserCog,
      title: "Account Management",
      description: "Update personal info, preferences, and account settings.",
      onClick: () => navigateTo('/profile'),
      bgColor: 'bg-gradient-to-br from-indigo-600 to-indigo-400'
    }
  ];



  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-teal-500 to-blue-700 text-white">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <h1 className="col-span-full text-4xl font-bold mb-8 animate-fadeIn">
          Welcome to Health AI, {userName}!
        </h1>
        
        {/* Left Container - Health Tips */}
        <div className="lg:col-span-2 h-[500px] bg-gray-800/50 rounded-2xl p-6 backdrop-blur-sm">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Your personalised health action plan</h2>
            <button
              onClick={generateHealthTips}
              disabled={generatingTips}
              className="group relative h-8 w-32 lg:w-36 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {generatingTips ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  <span className="animate-pulse">Generating...</span>
                </>
              ) : (
                <>
                  <span className="relative z-10">Generate Tips</span>
                  <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-10 transition-opacity"></div>
                </>
              )}
            </button>
          </div>
  
          <div className="h-[calc(100%-5rem)] relative">
            {generatingTips ? (
              <div className="flex flex-col items-center justify-center h-full space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-emerald-400/20 rounded-full animate-ping"></div>
                  <div className="relative z-10 w-16 h-16 bg-gray-700/50 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <Loader2 className="w-10 h-10 text-white animate-spin" />
                  </div>
                </div>
                <p className="text-white/90 text-sm font-medium text-center px-4 animate-pulse">
                  Generating personalized health insights...
                </p>
              </div>
            ) : (
              <div className="h-full overflow-y-auto pr-2">
                <div className="space-y-4">
                  {tips.length > 0 ? (
                    tips.map((tip, index) => (
                      <div
                        key={index}
                        className="bg-gray-700/10 p-5 rounded-xl backdrop-blur-sm transition-all duration-300 hover:bg-gray-600/50 break-words border border-gray-600 shadow-lg"
                      >
                        <p className="text-white/90 leading-relaxed">
                          <span className="font-bold mr-2">{index + 1}.</span>
                          {tip}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center bg-gray-700/50 backdrop-blur-sm rounded-xl p-6 border border-gray-600">
                      <p className="text-white/90 font-medium">
                        No health tips available. Click "Generate Tips" to get insights.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
  
        {/* Right Container - Tiles in 2x2 Grid */}
        <div className="lg:col-span-1">
          <div className="grid grid-cols-2 gap-4 h-[500px]">
            {tiles.map((tile, index) => (
              <HomePageTile 
                key={index} 
                {...tile} 
                className="h-[235px]" // Fixed height for 2x2 layout
              />
            ))}
          </div>
        </div>
      </main>
  
      <style jsx>{`
        .overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        .overflow-y-auto::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      `}</style>
      <Footer />
    </div>
  );
};

export default HomePage;